import { colors } from '@packages/core/styles';
import { RobotoFontFamily } from '@packages/ui/shared';
import { StyleSheet } from 'react-native';

/** The shared look of the connection badge label; only the color tells connected from disconnected. */
const connectionBadgeText = {
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 16,
    marginLeft: 8,
} as const;

/**
 * Text overrides that align the shared Typography variants with the SSM5 design tokens:
 * Heading02/Regular (27/40), Heading03/Semibold (21/32), Body/Regular (15/24),
 * Body/Semibold (15/24/600) and BodySmall/Semibold (13/16/600). The field message is
 * Body/Regular too, where the shared control defaults to the smaller caption size.
 * BodySmall/Regular (13/16) is the shared caption variant as it is. The DTC panel sets its codes and
 * module names in Body/Semibold on a 16px line, its badges in 11/16 Semibold and its links at 13/21.
 * The module status legend sets its title in 13/16 Regular and its chips in BodySmall/Semibold, in the
 * case the design writes them. The module view sets the module's full name in 13/21 Regular, its
 * General Info labels and values in Body/Semibold and its reprogramming notice and link in Body/Regular (15/24).
 * The freeze frame page uses Body/Semibold for the table headers and download toast, the field's
 * 15px text for PID search. Message titles cancel the shared bottom margin and keep a 4px gap only
 * when a description follows.
 */
export const ssm5TextStyles = StyleSheet.create({
    body: {
        lineHeight: 24,
    },
    bodySemibold: {
        fontWeight: '600',
        lineHeight: 24,
    },
    bodySemiboldTight: {
        fontFamily: RobotoFontFamily.Regular,
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 16,
    },
    carlineLabel: {
        flexShrink: 0,
        lineHeight: 24,
    },
    connectedBadgeText: { ...connectionBadgeText, color: colors.greenOne },
    disconnectedBadgeText: { ...connectionBadgeText, color: colors.blueOne },
    dtcBadgeText: {
        color: colors.black,
        fontFamily: RobotoFontFamily.Regular,
        fontSize: 11,
        fontWeight: '600',
        lineHeight: 16,
    },
    fieldError: {
        fontSize: 15,
        lineHeight: 24,
    },
    fieldLabel: {
        fontWeight: '400',
        lineHeight: 24,
    },
    fieldText: {
        fontFamily: RobotoFontFamily.Regular,
        fontSize: 15,
    },
    legendChipText: {
        color: colors.textDarkPrimary,
        fontFamily: RobotoFontFamily.Regular,
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 16,
        textTransform: 'none',
    },
    legendTitle: {
        fontSize: 13,
        fontWeight: '400',
        lineHeight: 16,
    },
    link: {
        lineHeight: 21,
    },
    messagePanelText: {
        maxWidth: 320,
    },
    moduleInfoText: {
        fontWeight: '600',
        lineHeight: 24,
    },
    moduleName: {
        fontSize: 13,
        fontWeight: '400',
        lineHeight: 21,
    },
    noticeText: {
        fontSize: 15,
        lineHeight: 24,
    },
    messageText: {
        lineHeight: 24,
        textAlign: 'center',
    },
    messageTitle: {
        fontWeight: '600',
        lineHeight: 32,
        marginBottom: 0,
        textAlign: 'center',
    },
    messageTitleWithDescription: {
        marginBottom: 4,
    },
    pageTitle: {
        lineHeight: 40,
    },
    primaryButtonTitle: {
        fontWeight: '600',
    },
    sectionTitle: {
        fontWeight: '600',
        lineHeight: 32,
    },
    smallButtonTitle: {
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 16,
    },
    stepBadgeText: {
        fontSize: 11,
        lineHeight: 16,
    },
    vinLink: {
        lineHeight: 24,
        width: 160,
    },
});

/**
 * Control overrides that align the shared Button, TextInputHelper, TextBadge and Icon with the SSM5
 * design: a 48px input field, 4px corner radius, the Brand/Disabled fill, the compact 32px tertiary
 * button, the 24px one of a DTC with its icon set against its title, the 40px icon button, the small 18px
 * badge of the DTC panel, the 20px chip of the module status legend and the 160px circle around the icon
 * of a panel message. The freeze frame
 * page adds a 340px width and 4px radius to the shared search field, a 24px clear action and a
 * Retry button with 15px side padding inside its outline, and centers the page loader with auto margins.
 * An action under a message has 24px above it.
 */
export const ssm5ControlStyles = StyleSheet.create({
    connectionBadge: {
        borderRadius: 100,
        paddingHorizontal: 8,
    },
    dtcActionButton: {
        borderRadius: 4,
        height: 24,
        paddingHorizontal: 4,
        paddingVertical: 0,
    },
    dtcActionButtonIcon: {
        marginLeft: 0,
    },
    dtcBadge: {
        borderRadius: 4,
        paddingHorizontal: 4,
        paddingVertical: 1,
    },
    field: {
        borderRadius: 4,
        height: 50,
        margin: -1,
    },
    freezeFramesClear: {
        height: 24,
        paddingHorizontal: 0,
        paddingVertical: 0,
        width: 24,
    },
    freezeFramesLoader: {
        margin: 'auto',
    },
    freezeFramesSearch: {
        borderRadius: 4,
        width: 340,
    },
    iconButton: {
        borderRadius: 4,
        height: 40,
        paddingHorizontal: 8,
        paddingVertical: 8,
        width: 40,
    },
    legendChip: {
        borderRadius: 4,
        paddingHorizontal: 4,
        paddingVertical: 2,
    },
    loaderBar: {
        width: 206,
    },
    messageAction: {
        marginTop: 24,
    },
    messageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 0,
        paddingVertical: 0,
    },
    messageIcon: {
        marginBottom: 24,
    },
    messageIllustration: {
        padding: 40,
    },
    outlineButton: {
        borderRadius: 4,
        paddingHorizontal: 15,
    },
    primaryButton: {
        borderRadius: 4,
    },
    primaryButtonDisabled: {
        backgroundColor: colors.brandDisabled,
        opacity: 1,
    },
    smallButton: {
        borderRadius: 4,
        height: 32,
        paddingHorizontal: 4,
        paddingVertical: 4,
    },
    stepBadge: {
        borderRadius: 4,
        paddingHorizontal: 8,
    },
});
